import { ArrowLeft } from 'lucide-react';
import Link from 'next/link';
import { type FC, type ReactNode } from 'react';

import { HEADER_HEIGHT } from '../constants/layout';

type Props = {
  title: string;
  subpages: SubpagesObjectType[];
  backButtonEnabled?: boolean;
  showHeader?: boolean;
  slug?: string;
  basePath: string;
  backPath?: string;
  mobileMode?: boolean;
};

export type SubpagesObjectType = {
  title: string;
  slug: string;
  icon: ReactNode;
  component: ReactNode;
  badge?: number;
};

const MultiPage: FC<Props> = ({
  title,
  subpages,
  backButtonEnabled = false,
  showHeader = true,
  slug,
  basePath,
  backPath = '/',
  mobileMode = false
}) => {
  const modeParam = mobileMode ? '&mode=mobile' : '';
  // Sticky headers sit below the AppLayout site header so they never cover it.
  // The aside is offset by HEADER_HEIGHT + the title-bar height (~56px).
  return (
    <section className="flex flex-col">
      {showHeader && (
        <div
          className="sticky z-10 border-b border-ds-border-subtle bg-background-primary px-4"
          style={{ top: HEADER_HEIGHT }}
        >
          <div className="flex items-center py-3">
            {backButtonEnabled && (
              <Link
                title="Back Button"
                className="mr-3 flex size-8 items-center justify-center rounded-md border-none bg-transparent transition-colors duration-150 hover:bg-background-light"
                href={backPath}
              >
                <ArrowLeft size={18} />
              </Link>
            )}
            <div className="flex flex-col">
              <h1 className="text-xl font-semibold tracking-heading-3 text-textPrimary">
                {title}
              </h1>
            </div>
          </div>
        </div>
      )}
      <div className="flex h-full flex-col md:flex-row">
        <aside
          className={`sticky z-10 flex shrink-0 flex-row overflow-x-auto border-b border-ds-border-subtle bg-background-primary ${mobileMode ? 'top-0 h-auto flex-wrap' : `h-12 md:w-14 md:flex-col md:overflow-x-hidden md:border-b-0 md:border-r md:border-r-ds-border-subtle laptop:w-48 ${showHeader ? 'top-[80px] md:h-[calc(100vh-72px)]' : 'top-[48px] md:h-[calc(100vh-48px)]'}`}`}
        >
          <nav
            className={`flex w-full ${mobileMode ? 'flex-row flex-wrap gap-1 px-2 py-2' : 'flex-row md:flex-col md:gap-1.5 md:px-2 md:py-3'}`}
          >
            {subpages.map((page) => (
              <Link
                key={page.slug}
                href={`/${basePath}/${page.slug}?backPath=${backPath}${modeParam}`}
                title={page.title}
                className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-emphasis text-textPrimary transition-colors duration-150 hover:bg-background-light ${mobileMode ? 'min-w-0' : 'min-w-16 justify-center gap-3 py-2.5 laptop:justify-start'} ${
                  page.slug === slug ? 'bg-background-light text-primary' : ''
                }`}
              >
                <div className="relative shrink-0">
                  {page.icon}
                  {typeof page.badge === 'number' && page.badge > 0 && (
                    <span className="absolute -right-1.5 -top-1.5 flex size-[18px] items-center justify-center rounded-full bg-orange-500 text-[10px] font-semibold text-white">
                      {page.badge > 9 ? '9+' : page.badge}
                    </span>
                  )}
                </div>
                {mobileMode ? (
                  <span className="flex items-center gap-2 text-xs">
                    {page.title}
                  </span>
                ) : (
                  <span className="hidden laptop:flex laptop:items-center laptop:gap-2">
                    {page.title}
                    {typeof page.badge === 'number' && page.badge > 0 && (
                      <span className="rounded-full bg-orange-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                        {page.badge}
                      </span>
                    )}
                  </span>
                )}
              </Link>
            ))}
          </nav>
        </aside>
        <section
          className={`min-w-0 flex-1 ${mobileMode ? 'overflow-x-hidden p-3' : 'overflow-x-auto p-6'}`}
        >
          {subpages.filter((page) => page.slug === slug)[0]?.component}
        </section>
      </div>
    </section>
  );
};

export default MultiPage;
